import type {
    AdditionalDriver,
    CardOnFile,
    Customer,
    FleetCategory,
    InsuranceProducts,
    Money,
    Payment,
    RentalAdditionalProduct,
    RentalExcessReduction,
    RentalLineItem,
    ReservationOptions,
    SecondaryProductItem,
    SecondaryProducts,
    SelfServiceReservation,
} from '@jucy/rentals-api-client';
import ReservationStatusType from '@jucy/rentals-common/data/ReservationStatusType';
import { makeAutoObservable } from 'mobx';
import type { DeepRequired } from 'utility-types';
import Product from './RentalsProductItem';

export default class RentalsReservation implements Omit<SelfServiceReservation, 'lines'> {
    quoteId?: string = undefined;
    reservationId?: number = undefined;
    reservationReference: string = '';
    onlineLogin?: string = undefined;
    quoteName?: string = undefined;
    pickUpLocationName?: string = undefined;
    pickUpLocationId?: string = undefined;
    pickUpLocationCode?: string = undefined;
    dropOffLocationName?: string = undefined;
    dropOffLocationId?: string = undefined;
    dropOffLocationCode?: string = undefined;
    pickUpDate?: Date = undefined;
    dropOffDate?: Date = undefined;
    rentalDays?: number = undefined;
    fleetCategory: DeepRequired<FleetCategory> = {} as unknown as DeepRequired<FleetCategory>;
    totalPrice: Required<Money> = { currencyCode: '', value: 0 };
    lines: Product[] = [];
    hirer?: Customer = undefined;
    additionalDrivers: Array<AdditionalDriver> = [];
    isVehicleAllocated?: boolean = undefined;
    rentalAgreementUrl?: string = undefined;
    insuranceProducts?: Array<DeepRequired<InsuranceProducts>> = undefined;
    secondaryProducts?: Array<SecondaryProducts> = undefined;
    payments?: Payment[] = undefined;
    cardOnFile?: CardOnFile = undefined;
    balanceDue?: DeepRequired<Money> = undefined;
    balanceDueWithCCFee?: DeepRequired<Money> = undefined;
    isWebAccount?: boolean = false;
    agentName?: string = undefined;
    options?: ReservationOptions = undefined;
    cCSurchargePercentage?: number = undefined;
    disableCheckIn?: boolean = undefined;

    checkInState?: Record<string, Date> = {};

    departureFlightNumber?: string = undefined;
    arrivalFlightNumber?: string = undefined;
    numberOfPeople: number = 1;
    hideBalanceDue = false;

    status?: ReservationStatusType = undefined;

    constructor(props?: Partial<SelfServiceReservation | RentalsReservation>) {
        makeAutoObservable(this);
        const { lines, ...values } = props || {};
        Object.assign(this, values);
        if (lines) {
            this.lines = (lines as unknown as RentalLineItem[]).reduce((acc, line) => {
                const existingLine = acc.find((l) => l.productId === line.productId && line.productCode !== 'CCFee');
                const allowMultiple = line.allowMultiple;
                let qty = allowMultiple && line.qty ? line.qty : 1;
                if (line.productType === 'Daily' && line.qty === line.rentalDays) {
                    qty = 1;
                }
                if (existingLine && allowMultiple) {
                    existingLine.quantity = existingLine.quantity + qty;
                    existingLine.total.value = (existingLine.total.value || 0) + (line.total?.value || 0);
                } else {
                    acc.push(
                        new Product({
                            ...line,
                            total: {
                                currencyCode: line.total ? line.total.currencyCode : line.price?.currencyCode,
                                value: line.total ? line.total.value : line.price?.value,
                            },
                            quantity: qty,
                        })
                    );
                }
                return acc;
            }, [] as Product[]);
        }
    }

    get insuranceCodes(): string[] {
        const codesAvailable = this.insuranceProducts?.[0]?.items.map((i) => i.productCode).filter((x) => x) || [];
        return Array.from(new Set(['PSER', ...codesAvailable]));
    }

    get includedLines(): Product[] {
        return this.lines?.filter((l) => !l.isExcluded && l.quantity > 0) || [];
    }

    get insuranceLine(): Product | undefined {
        return this.includedLines.find((l) => this.insuranceCodes.includes(l.productCode));
    }

    get extrasLines(): Product[] {
        return this.includedLines.filter((l) => !this.insuranceCodes.includes(l.productCode) && !l.isMandatory);
    }

    get mandatoryLines(): Product[] {
        return this.includedLines.filter((l) => l.isMandatory);
    }

    get computedTotal(): Required<Money> {
        const extrasTotal = this.includedLines.reduce((total, line) => total + (line?.total?.value || 0), 0);
        const merchantFeeTotal = this.payments?.reduce((total, payment) => total + (payment?.merchantFeeAmount?.value || 0), 0) || 0;
        const rentalTotal = this.fleetCategory.total?.value;
        return {
            currencyCode: this.totalPrice.currencyCode,
            value: Math.round((extrasTotal + merchantFeeTotal + rentalTotal + Number.EPSILON) * 100) / 100,
        };
    }

    get computedBalanceDue(): Required<Money> {
        return {
            currencyCode: this.totalPrice.currencyCode,
            value: this.hideBalanceDue ? 0 : this.computedTotal.value - this.paymentsTotal.value,
        };
    }

    get paymentsTotal(): Required<Money> {
        return {
            currencyCode: this.totalPrice.currencyCode,
            value: this.payments
                ? Math.round((this.payments.reduce((total, payment) => total + (payment.total?.value || 0), 0) + Number.EPSILON) * 100) / 100
                : 0,
        };
    }

    get availableProducts(): Array<RentalExcessReduction | RentalAdditionalProduct> {
        return [
            ...(this.insuranceProducts?.reduce((items, group) => [...items, ...group.items], [] as RentalExcessReduction[]) || []),
            ...(this.secondaryProducts?.reduce((items, group) => [...items, ...(group.items || [])], [] as RentalAdditionalProduct[]) || []),
        ];
    }

    mergeAdditionalItems(items: SecondaryProductItem[]): void {
        for (const item of items) {
            this.updateLineItem(item);
        }
        const insuranceItem = items.find((l) => l.code && this.insuranceCodes.includes(l.code) && !l.isExcluded && l.quantity && l.quantity > 0);

        if (insuranceItem) {
            this.updateInsurance(insuranceItem);
        }
    }

    updateLineItem(item: SecondaryProductItem): void {
        const productInfo = this.availableProducts.find((p) => p.productCode === item.code);
        const existingIndex = this.lines?.findIndex(
            (l) =>
                l.productId?.toLowerCase() === item.id?.toLowerCase() || (l.productCode && l.productCode?.toLowerCase() === item.code?.toLowerCase())
        );

        if (existingIndex && existingIndex < 0) {
            this.lines?.push(
                new Product({
                    productId: item.id,
                    productCode: item.code,
                    name: item.name,
                    category: productInfo?.category,
                    isMandatory: productInfo?.isMandatory,
                    description: productInfo?.description,
                    price: item.price,
                    total: item.total,
                    productType: productInfo?.productType,
                    allowMultiple: productInfo?.allowMultiple,
                    quantity: item.quantity,
                    rentalDays: item.rentalDays,
                    isExcluded: item.isExcluded,
                    excessType: (productInfo as RentalExcessReduction)?.excessType,
                    excess: (productInfo as RentalExcessReduction)?.excess,
                    apiOrder: item.sortOrder,
                })
            );
        } else if (existingIndex && this.lines) {
            this.lines[existingIndex].name = item.name as string;
            this.lines[existingIndex].price = item.price as Money;
            this.lines[existingIndex].total = item.total as Money;
            this.lines[existingIndex].quantity = item.quantity as number;
        }
    }

    updateInsurance(item: SecondaryProductItem): void {
        if (!this.lines) {
            return;
        }
        for (const line of this.lines) {
            if (this.insuranceCodes.includes(line.productCode)) {
                line.isExcluded = line.productCode !== item.code;
            }
        }
        this.updateLineItem(item);
    }
}

export type RentalsReservationInstance = DeepRequired<RentalsReservation>;
